import * as React from 'react';
import ContextConsumer from '../../landing_work/home/components/context';
import classnames from 'classnames';
import TextInput from 'components/form/text/text';
import Checkbox from 'components/form/checkbox/checkbox';
import Button from 'lj_shared/button/button';
import AuthButtons from './auth_buttons';
import { getCSRFToken } from 'lib/request_deprecated';
import { getQueryParams } from 'lib/utils';

const styles = require('./sign_up.module.scss');
const logo = require('images/lj_logo.png');

export interface SignUpProps {
  candidateSelected: boolean;
  editCompanyDisabled: boolean;
  urls: {
    loginUrl: string;
    newTalent: string;
    newCompanyUser: string;
    extraParams?: { [Identifier: string]: string };
  };
  socialUrls: {
    linkedin?: string;
    github: string;
    facebook: string;
    google: string;
    microsoft: string;
  };
  errors?: any;
  values: {
    name: string;
    company_name?: string;
    email: string;
    tos_consent: boolean;
  };
}

interface ToggleProps {
  candidateSelected: boolean;
  changeCandidateSelected: Function;
}

interface SignUpState {
  candidateSelected: boolean;
  isSubmitting: boolean;
}

class SignUp extends React.Component<SignUpProps, SignUpState> {
  constructor(props) {
    super(props);

    this.state = {
      candidateSelected: this.props.candidateSelected,
      isSubmitting: false,
    };

    this.signUpUrl = this.signUpUrl.bind(this);
    this.changeCandidateSelected = this.changeCandidateSelected.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  renderAlreadyHaveAccount() {
    return (
      <p className='.ld-medium-gray-text.ld-center-text.ld-login-warning.u-marginTop--large'>
        Already have an account?{' '}
        <a href={this.props.urls.loginUrl} className={styles.link}>
          Log in here
        </a>
      </p>
    );
  }

  changeCandidateSelected(candidateSelected) {
    this.setState({ candidateSelected });
  }

  handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    if (this.state.isSubmitting) {
      e.preventDefault();
      return;
    }
    this.setState({ isSubmitting: true });
  }

  render() {
    return (
      <ContextConsumer>
        {() => {
          return (
            <main className={styles.signUpMain} role='main' aria-label='Sign up'>
              <section className={styles.signUp}>
                <img className={styles.logo} src={logo} alt='Landing.Jobs' />
                <h1 className='ld-v-hidden'>Sign up</h1>
                <SignUpToggle
                  candidateSelected={this.state.candidateSelected}
                  changeCandidateSelected={this.changeCandidateSelected}
                />
                <SignUpForm
                  editCompanyDisabled={this.props.editCompanyDisabled}
                  loginUrl={this.props.urls.loginUrl}
                  signUpUrl={this.signUpUrl()}
                  company={!this.state.candidateSelected}
                  errors={this.props.errors}
                  values={this.props.values}
                  onSubmit={this.handleSubmit}
                  isSubmitting={this.state.isSubmitting}
                />
                {this.state.candidateSelected && (
                  <AuthButtons
                    kind='Sign up'
                    urls={this.props.socialUrls}
                    excludeProviders={['Facebook']}
                  />
                )}
                {this.renderAlreadyHaveAccount()}
              </section>
              <div className={styles.background} />
            </main>
          );
        }}
      </ContextConsumer>
    );
  }

  getExtraParams = (): string => {
    if (!this.props.urls.extraParams || !Object.keys(getQueryParams())) {
      return '';
    } else {
      const extraParams = { ...this.props.urls?.extraParams, ...getQueryParams() };
      return `?${new URLSearchParams(extraParams).toString()}`;
    }
  };

  signUpUrl = () => {
    return this.state.candidateSelected
      ? this.props.urls.newTalent + this.getExtraParams()
      : this.props.urls.newCompanyUser;
  };
}

const SignUpToggle = (props: ToggleProps) => {
  const { candidateSelected, changeCandidateSelected } = props;

  const classes = classnames(styles.toggle, styles.jobsToggle);

  const handleJobSeekerClick = () => {
    changeCandidateSelected(true);
  };

  const handleJobSeekerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleJobSeekerClick();
    }
  };

  const handleEmployerClick = () => {
    changeCandidateSelected(false);
  };

  const handleEmployerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleEmployerClick();
    }
  };

  return (
    <div className={styles.toggleBar} role='tablist' aria-label='Account type selection'>
      <div
        className={classnames(classes, candidateSelected && styles.active)}
        onClick={() => changeCandidateSelected(true)}
        role='button'
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); changeCandidateSelected(true); } }}
      >
        I'm a Job Seeker
      </div>

      <div
        className={classnames(classes, !candidateSelected && styles.active)}
        onClick={() => changeCandidateSelected(false)}
        role='button'
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); changeCandidateSelected(false); } }}
      >
        I'm an Employer
      </div>
    </div>
  );
};

const SignUpForm = props => {
  const { editCompanyDisabled, company, signUpUrl, errors, values, onSubmit, isSubmitting } = props;
  const formName = company ? 'company_signup_form' : 'candidate_signup_form';

  return (
    <div className='.ld-container.ld-wizard-container.ld-auth-box'>
      <form
        action={signUpUrl}
        method='post'
        onSubmit={onSubmit}
        name={formName}
        id={formName}
        aria-label={company ? 'Company sign up form' : 'Candidate sign up form'}
      >
        <input name='authenticity_token' value={getCSRFToken()} type='hidden' />

        {company && (
          <TextInput
            disabled={editCompanyDisabled}
            label='Company name'
            name='company_name'
            placeholder='Company Name'
            color='tuftsBlue'
            error={errors && errors.company_name}
            value={values && values.company_name}
            required={true}
          />
        )}
        <TextInput
          label='First and last name'
          name='name'
          placeholder='First and last name'
          color='tuftsBlue'
          error={errors && errors.name}
          value={values && values.name}
          autocomplete='new-password'
          required={true}
        />
        <TextInput
          label='Email'
          name='email'
          placeholder='Email'
          color='tuftsBlue'
          type='email'
          error={errors && errors.email}
          value={values && values.email}
          required={true}
        />
        <TextInput
          label='Password'
          name='password'
          placeholder='Password'
          color='tuftsBlue'
          type='password'
          error={errors && errors.password}
          onFocusPopUp='Your password must have 8 or more characters'
          required={true}
        />
        <Checkbox
          name='tos_consent'
          id='tos_consent'
          controlled={false}
          value='true'
          className={styles.checkbox}
          label={
            <>
              By signing up you agree to our{' '}
              <a href='/tos' target='_blank' className={styles.link}>
                Terms & Conditions
              </a>
              .
            </>
          }
          error={errors && errors.tos_consent}
          checked={values && !!values.tos_consent}
        />
        <Button
          buttonColor={company ? 'ripePlum' : 'tuftsBlue'}
          otherClasses={styles.button}
          isButton={true}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Signing up...' : 'Sign up'}
        </Button>
      </form>
    </div>
  );
};

export default SignUp;
